import React, { forwardRef, useContext, useEffect } from 'react'
import { formatDate } from '../../assets/constants/date'
import CustomImage from '../customImage/customImage'
import type { IEvent } from '../../api/dto/IEvent'
import { InitialStateContext } from '../../App'
import { GameStatus } from '../../assets/constants/gameStatus'
import { ArrowRight } from '../../assets/icons/arrowRight'
import { LiveIcon } from '../../assets/icons/liveIcon'

interface IProps {
    match: IEvent
    onClick: () => void
    isSelected?: boolean
    ref?: React.RefObject<HTMLDivElement>
}

interface ITeamProps {
    teamName: string
    teamLogo: string
}

const getGameStatus = (isLive: boolean, isActive?: boolean): GameStatus => {
    switch (true) {
        case isActive:
            return GameStatus.WATCHING
        case isLive:
            return GameStatus.LIVE
        default:
            return GameStatus.UPCOMING
    }
}

const getIconByStatus = (status: GameStatus) => {
    switch (status) {
        case GameStatus.LIVE:
            return <LiveIcon className="ml-[12px] w-[20px] h-[11px]" />
        case GameStatus.WATCHING:
            return null
        case GameStatus.UPCOMING:
            return (
                <ArrowRight className="ml-[12px] w-[9px] h-[10px] fill-cyan stroke-cyan" />
            )
        default:
            return (
                <ArrowRight className="ml-[12px] w-[9px] h-[10px] fill-cyan stroke-cyan" />
            )
    }
}

const TeamBlock = ({ teamLogo, teamName }: ITeamProps) => (
    <div
        title={teamName}
        className="order-5 flex text-white flex-row-reverse items-center justify-end w-full z-10 gap-3"
    >
        <div className="text-[16px] font-poppinsRegular whitespace-nowrap overflow-hidden text-ellipsis ">
            {teamName}
        </div>
        <div className="h-[21px] w-[21px]">
            <CustomImage
                src={teamLogo}
                width={32}
                height={32}
                className="h-[21px] w-[21px]"
                mockImg="/images/mockLogos/noTeam.svg"
                alt={teamName}
            />
        </div>
    </div>
)

const GameTableItem = forwardRef<HTMLDivElement, IProps>(
    ({ match, onClick, isSelected }, ref) => {
        const { differanceTimeZoneInMs } = useContext(InitialStateContext)

        const gtmTime = Math.floor(new Date().getTime() / 1000)
        const isMatchStart = gtmTime >= match.start_time
        const isMatchEnd = match.end_time && gtmTime >= match.end_time
        const isLive = isMatchStart && !isMatchEnd

        const gameStatus = getGameStatus(isLive, isSelected)
        const statusIcon = getIconByStatus(gameStatus)

        return (
            <div
                ref={ref}
                // id={match.uuid}
                onClick={onClick}
                className={`cursor-pointer ${isSelected ? 'bg-darkBlueOpacity1' : ''} px-3 pt-1 pb-3 w-full hover:bg-darkBlueOpacity1 rounded-lg h-[120px] mb-[10px] flex flex-col gap-3 tableItem  ${isLive ? 'border-cyan border-2' : 'border-b-2 border-darkBlueOpacity0'}`}
            >
                <div id="allHidden" className="hidden" />
                <div className="flex items-center">
                    <p className="m-0 flex flex-row items-center w-full h-full gap-4">
                        <span className="text-white text-[16px] font-poppinsBold">
                            {formatDate(
                                new Date(
                                    match.start_time * 1000 +
                                        differanceTimeZoneInMs
                                ),
                                'HH:mm'
                            )}
                        </span>
                        <span className="whitespace-nowrap text-white opacity-[0.5] text-[14px] font-poppinsRegular">
                            {formatDate(
                                new Date(
                                    match.start_time * 1000 +
                                        differanceTimeZoneInMs
                                ),
                                'ddd MMM yyyy'
                            )}
                        </span>
                    </p>
                    <div className="items-center flex justify-end w-full h-full">
                        <span
                            className={`text-[14px] font-poppinsBold whitespace-nowrap overflow-hidden text-ellipsis text-${gameStatus === GameStatus.UPCOMING ? 'cyan' : 'white'}`}
                        >
                            {gameStatus}
                        </span>
                        {statusIcon}
                    </div>
                </div>
                <div className="flex flex-col col-span-2 justify-center gap-3">
                    <TeamBlock
                        teamName={match.participantHome.name}
                        teamLogo={match?.participantHome.logo}
                    />
                    <TeamBlock
                        teamName={match.participantAway.name}
                        teamLogo={match?.participantAway.logo}
                    />
                </div>
            </div>
        )
    }
)

export default GameTableItem
